import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Heart, ShoppingBag, Palette } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function StyleDiscoveryGuide() {
  const navigate = useNavigate();

  const styleArchetypes = [
    {
      name: "Classic Elegant",
      description: "Timeless pieces, neutral palette, quality over quantity. Think Audrey Hepburn meets modern sophistication.",
      keyPieces: ["Tailored blazer", "White button-down", "Little black dress", "Cashmere sweater", "Pearl or gold jewelry"],
      color: "slate"
    },
    {
      name: "Relaxed Chic",
      description: "Effortless style that looks polished but feels comfortable. California-cool meets European ease.",
      keyPieces: ["Perfect-fit jeans", "Linen pieces", "Soft knits", "White sneakers", "Layered necklaces"],
      color: "sky"
    },
    {
      name: "Bold & Expressive",
      description: "Not afraid of color, pattern, or making a statement. Fashion as self-expression and joy.",
      keyPieces: ["Statement coats", "Printed pieces", "Bold accessories", "Interesting textures", "Conversation-starting jewelry"],
      color: "fuchsia"
    },
    {
      name: "Modern Minimalist",
      description: "Clean lines, architectural shapes, monochromatic palette. Less is more, quality is everything.",
      keyPieces: ["Structured trousers", "Oversized blazers", "Tonal outfits", "Geometric jewelry", "Sleek bags"],
      color: "zinc"
    },
    {
      name: "Romantic Feminine",
      description: "Soft fabrics, flowing silhouettes, delicate details. Embracing femininity with sophistication.",
      keyPieces: ["Wrap dresses", "Silk blouses", "Midi skirts", "Floral prints", "Delicate jewelry"],
      color: "rose"
    },
    {
      name: "Polished Professional",
      description: "Power dressing with personality. Commanding respect while expressing personal style.",
      keyPieces: ["Sheath dresses", "Power suits", "Silk scarves", "Structured bags", "Quality heels"],
      color: "indigo"
    }
  ];

  const wardrobeEssentials = [
    { category: "Tops", items: ["White t-shirt (quality cotton)", "Black t-shirt", "Silk or satin blouse", "Cashmere sweater", "Crisp white button-down"] },
    { category: "Bottoms", items: ["Dark jeans that fit perfectly", "Black trousers", "Midi skirt", "Comfortable joggers (elevated)", "Tailored shorts"] },
    { category: "Layers", items: ["Navy or camel blazer", "Classic trench coat", "Cozy cardigan", "Denim jacket", "Leather or faux-leather jacket"] },
    { category: "Shoes", items: ["White sneakers", "Black ankle boots", "Nude heels", "Comfortable flats", "Stylish sandals"] }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="mb-4 text-secondary-700 hover:text-primary"
          >
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-pink-100 text-pink-700 rounded-full text-sm font-semibold mb-4">
            Week 7 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Discovering Your Personal Style
          </h1>
          <p className="text-xl text-secondary-600">
            Dress Like the Woman You're Becoming
          </p>
        </div>

        {/* Hero Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl mb-8">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b182c6e0-1353-47f6-8a97-d98fdf4ac183/images/3e720d16e3612d8ade1ef189726fdd5abd202fb76af3cfce93c2bec53ece127d.png"
            alt="Style Discovery"
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Introduction */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">
            Why Style Matters in Your 40s
          </h2>
          <p className="text-secondary-700 mb-4">
            Your 40s are the perfect time to refine your personal style. You know who you are now—your body, your preferences, your lifestyle. Instead of chasing trends or dressing for others, you can finally build a wardrobe that feels authentically <em>you</em>.
          </p>
          <p className="text-secondary-700 mb-4">
            Great style isn't about following rules or spending a fortune. It's about understanding what makes you feel confident and creating a closet that supports your daily life—not an imaginary one.
          </p>
          <div className="bg-pink-50 rounded-xl p-4 border border-pink-200">
            <p className="text-secondary-800 font-medium flex items-start gap-2">
              <Heart className="w-5 h-5 text-pink-500 mt-1 flex-shrink-0" />
              The goal isn't to look younger or thinner—it's to look like the most polished, confident version of yourself.
            </p>
          </div>
        </section>

        {/* Style Archetypes */}
        <section className="mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 text-center">
            Find Your Style Archetype
          </h2>
          <p className="text-secondary-600 text-center mb-6">
            Which description resonates most with you? You might be a blend of two!
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {styleArchetypes.map((style, index) => (
              <div key={index} className={`bg-white rounded-xl p-6 shadow-md border-2 border-${style.color}-200 hover:shadow-lg transition-shadow`}>
                <h3 className={`font-bold text-${style.color}-700 text-lg mb-2 flex items-center gap-2`}>
                  <Sparkles className={`w-5 h-5 text-${style.color}-500`} />
                  {style.name}
                </h3>
                <p className="text-secondary-600 text-sm mb-4">{style.description}</p>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-semibold text-secondary-500 uppercase mb-2">Key Pieces:</p>
                  <ul className="flex flex-wrap gap-2">
                    {style.keyPieces.map((piece, i) => (
                      <li key={i} className={`text-xs bg-${style.color}-100 text-${style.color}-700 px-2 py-1 rounded-full`}>
                        {piece}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Wardrobe Essentials */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <ShoppingBag className="w-8 h-8 text-pink-500" />
            <h2 className="text-2xl font-serif font-bold text-secondary-900">
              Wardrobe Essentials for Every Style
            </h2>
          </div>
          <p className="text-secondary-600 mb-6">
            Regardless of your style archetype, these versatile pieces form a solid foundation:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {wardrobeEssentials.map((category, index) => (
              <div key={index} className="bg-pink-50 rounded-xl p-5">
                <h3 className="font-bold text-pink-700 mb-3">{category.category}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                      <span className="w-2 h-2 bg-pink-400 rounded-full"></span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Style Tips */}
        <section className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 border-2 border-pink-200 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Palette className="w-8 h-8 text-pink-500" />
            <h2 className="text-2xl font-serif font-bold text-secondary-900">
              Style Tips for Your 40s
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: "Invest in Fit", tip: "Nothing looks as expensive as clothes that fit perfectly. Budget for tailoring—it's worth it." },
              { title: "Know Your Colors", tip: "Discover which colors make your skin glow. Wear them near your face." },
              { title: "Quality Over Quantity", tip: "One beautiful piece beats five cheap ones. Build slowly." },
              { title: "Comfort is Non-Negotiable", tip: "If you can't move, sit, or breathe, you won't feel confident." },
              { title: "Dress for Your Real Life", tip: "Build a wardrobe for the life you actually live, not a fantasy one." },
              { title: "Accessories Elevate Everything", tip: "A simple outfit becomes stylish with the right jewelry, bag, or scarf." }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4 shadow-sm">
                <h3 className="font-bold text-pink-700 mb-1">{item.title}</h3>
                <p className="text-sm text-secondary-600">{item.tip}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Exercise */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 text-center">
            Your Style Discovery Exercise
          </h2>
          <p className="text-secondary-600 text-center mb-6">
            Complete these steps to clarify your personal style:
          </p>
          <div className="space-y-4">
            {[
              "Gather inspiration: Save 20 outfit images that excite you (Pinterest, Instagram, magazines)",
              "Look for patterns: What colors, silhouettes, and vibes keep appearing?",
              "Audit your closet: What do you actually wear and feel great in? What never gets touched?",
              "Identify your lifestyle needs: What percentage is work/casual/dressy?",
              "Define your style in 3 words: (e.g., 'Relaxed, Polished, Feminine')",
              "Create a 'shopping list' of pieces that would fill gaps in your current wardrobe"
            ].map((step, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-pink-50 rounded-lg">
                <span className="w-6 h-6 bg-pink-200 rounded-full flex items-center justify-center text-pink-700 font-bold text-sm flex-shrink-0">
                  {index + 1}
                </span>
                <span className="text-secondary-700">{step}</span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default StyleDiscoveryGuide;
